import "./exports.less";
import template from "./exports.html";
import {
	FuzzySearcher,
	FUZZY_OPTIONS
} from "../../utils/fuzzy-search";
import { FORM_EXPORT_LOG_PAGE } from "../../routes";
import { globs } from "../../services/globs";
import {
	ActionOrPattern,
	Any
} from "../../services/security/actionScopes";
import {
	FILTER_OPERATORS, 
	SORT_DIRECTIONS 
} from "./constants";
import moment from "moment";
import { debounce } from "lodash";
import { createExportModal } from "./create-new-export";
import { viewExportModal } from "./view-export";

const allowedConfigsToCreate = ["BYCHANGED"];

function controller(
	appState, 
	loadingService, 
	tableHelper, 
	$uibModal, 
	$logger, 
	api, 
	securityService,
	$state
) {
	const ctrl = this;

	ctrl._uibModal = $uibModal;
	ctrl.onCollapse = onCollapse;
	ctrl.tableWidthCss = "col-sm-9";

	ctrl.searcher = new FuzzySearcher([], FUZZY_OPTIONS.EXPORTS);

	ctrl.table = {
		data: [],
		actions: {},
		sort: {
			predicate: "Id",
			direction: "asc"
		},
		filter: () => { 
			tableHelper.filter(ctrl.table, ctrl.searcher, { defaultSortBy: "Id" }); 
			ctrl.paginatorOptions = {
				...ctrl.paginatorOptions, 
				totalItems: ctrl.totalLogs ?? 0
			};
		},
		reset: () => reset(),
		filterBy: null
	};
	ctrl.paginatorOptions = {};
	ctrl.exportConfigurations = [];
	ctrl.canCreateExport = globs.exports("*").create;
	ctrl.hasAnyUxAction = new Any([
		new ActionOrPattern(this.canCreateExport)
	]);

	ctrl.isByChangedConfig = false;

	ctrl.$onInit = () => {
		ctrl.form = appState.forms.selected;

		setDefaultFilters();

		getExports(ctrl.paginatorOptions?.currentPage ?? 1, ctrl.paginatorOptions?.itemsPerPage ?? 10);
	};

	ctrl.view = (exportData) => {
		$uibModal.open({
			component: "view-export",
			size: "xlg",
			resolve: {
				selectedConfig: () => ctrl.filter.selectedExportConfig,
				exportLog: () => exportData
			},
			keyboard: false,
			backdrop: "static"
		}).result.then(() => {
		});
	};

	ctrl.tableDataUpdate = () => {
		getExports(ctrl.paginatorOptions.currentPage, ctrl.paginatorOptions.itemsPerPage);
	};

	ctrl.updateSearchFilter = debounce(ctrl.tableDataUpdate, 500, { "leading": false, "trailing": true });

	ctrl.clearSearchFilter = () => {
		if(ctrl.filter.search) {
			delete ctrl.filter.search;
			ctrl.tableDataUpdate();
		}
	};

	ctrl.updateDateFilter = (startDate, endDate) => {
		if(startDate || endDate) {
			ctrl.filter.startDate = startDate || ctrl.filter.startDate;
			ctrl.filter.endDate = endDate || ctrl.filter.endDate;
		}

		ctrl.tableDataUpdate();
	};

	ctrl.onExportSelectionChanged = (selection) => {
		ctrl.filter.selectedExportConfig = selection?.Code;
		ctrl.isByChangedConfig = allowedConfigsToCreate.indexOf(selection.Type.Code) > -1;

		ctrl.tableDataUpdate();
	};

	ctrl.createNewExport = () => {
		$uibModal.open({
			component: "create-new-export",
			size: "xlg",
			resolve: {
				selectedConfig: () => ctrl.filter.selectedExportConfig,
				availableConfigs: () => ctrl.exportConfigurations
			},
			keyboard: false,
			backdrop: "static"
		}).result.then(() => {
			getExports(ctrl.paginatorOptions?.currentPage ?? 1, ctrl.paginatorOptions?.itemsPerPage ?? 10);
		});
	};

	async function getExports(page, count) {
		if(ctrl.filter.selectedExportConfig === undefined) {
			ctrl.totalLogs = 0;
			setTableData([]);

			return;
		}

		const { start, end } = getDateRangeFilter();

		const Filter = {
			And: [
				{
					Created: {
						[FILTER_OPERATORS.lessThan.code]: end.toISOString()
					}
				},
				{
					Created: {
						[FILTER_OPERATORS.greaterThanOrEqualTo.code]: start.toISOString()
					}
				},
				{
					Configuration: {
						Code: {
							[FILTER_OPERATORS.equals.code]: ctrl.filter.selectedExportConfig
						}
					}
				}
			]
		};
		if(ctrl.filter.search) {
			Filter.And.push({ 
				Id: { 
					[FILTER_OPERATORS.equals.code]: ctrl.filter.search 
				} 
			});
		}

		const logs = api.post(FORM_EXPORT_LOG_PAGE(ctrl.form.Id), {
			Filter,
			Sort: {
				Direction: SORT_DIRECTIONS.ascending.code,
				By: "Id"
			},
			Page: {
				Count: count,
				Page: page
			}
		});

		ctrl.currentLogs = await loadingService.watch(logs);
		if(page === 1) {
			ctrl.totalLogs = ctrl.currentLogs.Total;
		}

		const allowedLogs = (ctrl.currentLogs.Data ?? []).filter(l => {
			const isAllowed = securityService.isActionAllowed(globs.exports(l.Configuration.Code).logs(l.Id).read);
			
			return isAllowed;
		});

		setTableData(allowedLogs);
	}

	function getDateRangeFilter() { 
		//if this is custom entry, then it will at least get rounded to the next mionute
		//if this is a range, and passed the last second/millisecond of the range, will be rounded to the next probable minute intended
		const start = moment(ctrl.filter.startDate);
		const end = moment(ctrl.filter.endDate);

		const startDate = start;
		const endDate = end.second() != 0 || end.millisecond() != 0
			? end.add(1, "minute")
				.startOf("minute")
			: end;

		return {
			start: startDate,
			end: endDate
		};
	}

	function setDefaultFilters() {
		const dateStart = $state.params.start;
		const dateEnd = $state.params.end;

		const start = dateStart ? moment(dateStart, "YYYY/MM/DD") : moment().startOf("month");
		const end = dateEnd ? moment(dateEnd, "YYYY/MM/DD") : moment().endOf("month");
		ctrl.filter = {
			startDate: start.toDate(),
			endDate: end.toDate()
		};
	}

	function setTableData(exportItems) {
		ctrl.table.data = exportItems;
		ctrl.searcher.setCollection(exportItems);
		ctrl.paginatorOptions = {
			...ctrl.paginatorOptions, 
			totalItems: ctrl.totalLogs ?? 0
		};
	}

	function reset() {
		ctrl.table.filterBy = null;
		ctrl.table.filter();
	}

	function onCollapse() {
		ctrl.tableWidthCss = ctrl.isCollapsed ? "col-sm-12" : "col-sm-9";
	}
}

export default angular
	.module("vesl.screens.exports", [createExportModal, viewExportModal])
	.component("exports", {
		template,
		controller
	}).name;
